import React, { useState } from "react";
import HoldingsSection from "./holding_section";
import GoldCard from "./metal_card";
import DiamondPointsCard from "./diamond_points_section";
import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchThemeRequest,
  createThemeRequest,
  clearThemeMessages,
} from "../../../redux/AppTheme/AppThemeSlice";
import { useEffect } from "react";
import { canShowPermission } from "../Permissions/permissionObject";
const colorConfigs = [
  {
    key: "buttonColor",
    label: "Button Color",
    default: "#250164",
    description: "Color for buttons and interactive elements.",
  },
  {
    key: "containerBorderColor",
    label: "Container Border Color",
    default: "#E0E0E0",
    description: "Color for container borders.",
  },
  {
    key: "appBarColor",
    label: "App Bar Color",
    default: "#250164",
    description: "Color for app bar and status bar.",
  },
  {
    key: "textColor",
    label: "Text Color",
    default: "#000000",
    description: "Color for text in the app.",
  },
  {
    key: "svgColor",
    label: "SVG Color",
    default: "#000000",
    description: "Color for SVG icons in the app.",
  },
  {
    key: "bottomNavBarBackgroundColor",
    label: "Bottom NavBar Background",
    default: "#FFFFFF",
    description: "Background color for bottom navigation bar.",
  },
  {
    key: "bottomNavBarSelectedColor",
    label: "Bottom NavBar Selected",
    default: "#250164",
    description: "Color for selected tab in bottom navigation.",
  },
  {
    key: "bottomNavBarUnselectedColor",
    label: "Bottom NavBar Unselected",
    default: "#666666",
    description: "Color for unselected tabs in bottom navigation.",
  },
];

export default function AppThemeHistory() {
  const dispatch = useDispatch();
  const themeState = useSelector((state) => state.theme);
  useEffect(() => {
    dispatch(fetchThemeRequest()); //  fetch server theme
  }, [dispatch]);
  const [loading, setLoading] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);

  const getThemeList = () => {
    const raw = themeState?.theme;
    if (Array.isArray(raw)) return raw; // already array
    if (Array.isArray(raw?.data)) return raw.data; // nested in .data
    return []; // fallback
  };

  // Helper to parse gradient colors - support both array and start/end format
  const parseGradientColors = (startKey, endKey, defaultColors) => {
    return (t0) => {
      if (Array.isArray(t0[`${startKey.replace('StartColor', '')}GradientColors`])) {
        return t0[`${startKey.replace('StartColor', '')}GradientColors`];
      }
      if (t0[startKey] && t0[endKey]) {
        return [t0[startKey], t0[endKey]];
      }
      return defaultColors;
    };
  };

  // Initial effect – only inactive records
  useEffect(() => {
    const list = getThemeList();
    if (list.length > 0) {
      setCurrentIndex(0);
      const t0 = list[0];
      setTheme({
        scaffoldBackgroundColor:
          t0.scaffoldBackgroundColor || t0.scaffoldColor || "#FFFFFF",
        scaffoldGradientColors: parseGradientColors(
          "scaffoldStartColor",
          "scaffoldEndColor",
          t0.scaffoldGradientColors || ["#FFFFFF", "#F5F5F5"]
        )(t0),
        buttonColor: t0.primaryColor || t0.buttonColor || "#250164",
        containerColor: t0.containerColor || "#F5F5F5",
        containerBorderColor: t0.containerBorder || t0.containerBorderColor || "#E0E0E0",
        appBarColor: t0.appBarColor || "#250164",
        textColor: t0.textColor || "#000000",
        svgColor: t0.svgColor || "#000000",
        bottomNavBarBackgroundColor: t0.bottomNavBarBackgroundColor || "#FFFFFF",
        bottomNavBarSelectedColor: t0.bottomNavBarSelectedColor || "#250164",
        bottomNavBarUnselectedColor: t0.bottomNavBarUnselectedColor || "#666666",
        smallContainerGradientColors: parseGradientColors(
          "smallContainerStartColor",
          "smallContainerEndColor",
          ["#7A3FFF", "#9E3ED7"]
        )(t0),
        mediumContainerGradientColors: parseGradientColors(
          "mediumContainerStartColor",
          "mediumContainerEndColor",
          ["#5A2FCF", "#8E2EC7"]
        )(t0),
        bigContainerGradientColors: parseGradientColors(
          "bigContainerStartColor",
          "bigContainerEndColor",
          ["#4A1FBF", "#7E1EB7"]
        )(t0),
      });
    }
  }, [themeState.theme]);

  const [theme, setTheme] = useState({
    scaffoldBackgroundColor: "#FFFFFF",
    scaffoldGradientColors: ["#FFFFFF", "#F5F5F5"],
    buttonColor: "#250164",
    containerColor: "#F5F5F5",
    containerBorderColor: "#E0E0E0",
    appBarColor: "#250164",
    textColor: "#000000",
    svgColor: "#000000",
    bottomNavBarBackgroundColor: "#FFFFFF",
    bottomNavBarSelectedColor: "#250164",
    bottomNavBarUnselectedColor: "#666666",
    smallContainerGradientColors: ["#7A3FFF", "#9E3ED7"],
    mediumContainerGradientColors: ["#5A2FCF", "#8E2EC7"],
    bigContainerGradientColors: ["#4A1FBF", "#7E1EB7"],
  });

  const handleColorChange = (key, value) => {
    setTheme({ ...theme, [key]: value });
  };

  const handleGradientColorChange = (containerKey, index, value) => {
    const updatedColors = [...theme[containerKey]];
    updatedColors[index] = value;
    setTheme({ ...theme, [containerKey]: updatedColors });
  };

  const addGradientColor = (containerKey) => {
    const currentColors = theme[containerKey] || [];
    const newColor = currentColors.length > 0 
      ? currentColors[currentColors.length - 1] 
      : "#FFFFFF";
    setTheme({ 
      ...theme, 
      [containerKey]: [...currentColors, newColor] 
    });
  };

  const removeGradientColor = (containerKey, index) => {
    const currentColors = theme[containerKey] || [];
    if (currentColors.length > 2) {
      const updatedColors = currentColors.filter((_, i) => i !== index);
      setTheme({ ...theme, [containerKey]: updatedColors });
    }
  };

  const buildGradientString = (colors, direction = "135deg") => {
    if (!colors || colors.length === 0) return `linear-gradient(${direction}, #7A3FFF, #9E3ED7)`;
    if (colors.length === 1) return `linear-gradient(${direction}, ${colors[0]}, ${colors[0]})`;
    
    const colorStops = colors.map((color, index) => {
      const percentage = (index / (colors.length - 1)) * 100;
      return `${color} ${percentage}%`;
    }).join(", ");
    
    return `linear-gradient(${direction}, ${colorStops})`;
  };

  const buildScaffoldGradientString = (colors) => {
    return buildGradientString(colors, "to bottom");
  };

  const showThemeAt = (idx) => {
    const list = getThemeList();
    if (!list.length) return;
    const n = ((idx % list.length) + list.length) % list.length;
    const t = list[n];
    setCurrentIndex(n);
    setTheme({
      scaffoldBackgroundColor:
        t.scaffoldBackgroundColor || t.scaffoldColor || "#FFFFFF",
      scaffoldGradientColors: parseGradientColors(
        "scaffoldStartColor",
        "scaffoldEndColor",
        t.scaffoldGradientColors || ["#FFFFFF", "#F5F5F5"]
      )(t),
      buttonColor: t.primaryColor || t.buttonColor || "#250164",
      containerColor: t.containerColor || "#F5F5F5",
      containerBorderColor: t.containerBorder || t.containerBorderColor || "#E0E0E0",
      appBarColor: t.appBarColor || "#250164",
      textColor: t.textColor || "#000000",
      svgColor: t.svgColor || "#000000",
      bottomNavBarBackgroundColor: t.bottomNavBarBackgroundColor || "#FFFFFF",
      bottomNavBarSelectedColor: t.bottomNavBarSelectedColor || "#250164",
      bottomNavBarUnselectedColor: t.bottomNavBarUnselectedColor || "#666666",
      smallContainerGradientColors: parseGradientColors(
        "smallContainerStartColor",
        "smallContainerEndColor",
        ["#7A3FFF", "#9E3ED7"]
      )(t),
      mediumContainerGradientColors: parseGradientColors(
        "mediumContainerStartColor",
        "mediumContainerEndColor",
        ["#5A2FCF", "#8E2EC7"]
      )(t),
      bigContainerGradientColors: parseGradientColors(
        "bigContainerStartColor",
        "bigContainerEndColor",
        ["#4A1FBF", "#7E1EB7"]
      )(t),
    });
  };

  const handlePrev = () => showThemeAt(currentIndex - 1);
  const handleNext = () => showThemeAt(currentIndex + 1);

  function buildThemePayload() {
    return {
      scaffoldColor: theme.scaffoldBackgroundColor,
      scaffoldGradientColors: theme.scaffoldGradientColors,
      primaryColor: theme.buttonColor,
      containerColor: theme.containerColor,
      containerBorder: theme.containerBorderColor,
      appBarColor: theme.appBarColor,
      textColor: theme.textColor,
      svgColor: theme.svgColor,
      bottomNavBarBackgroundColor: theme.bottomNavBarBackgroundColor,
      bottomNavBarSelectedColor: theme.bottomNavBarSelectedColor,
      bottomNavBarUnselectedColor: theme.bottomNavBarUnselectedColor,
      smallContainerGradientColors: theme.smallContainerGradientColors,
      mediumContainerGradientColors: theme.mediumContainerGradientColors,
      bigContainerGradientColors: theme.bigContainerGradientColors,
      brightness: "light",
    };
  }
  function saveTheme() {
    const confirmSave = window.confirm(
      "Are you sure you want to apply the new theme changes from history?"
    );
    if (!confirmSave) return;

    setLoading(true); // start loader 🚀

    const payload = buildThemePayload();
    dispatch(createThemeRequest(payload));
    toast.success("App Theme Saved Successfully");
    // fallback in case Redux never responds
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  }

  useEffect(() => {
    if (themeState.message) {
      toast.success(themeState.message);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
    if (themeState.error) {
      toast.error(themeState.error);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
  }, [themeState.message, themeState.error, dispatch]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);
  const canCreateTheme = canShowPermission(permissions, "appThemeSelector", "create");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        paddingTop: "70px",
      }}
    >
      {/* Color selection panel */}
      <div
        style={{
          flex: 2.5,
          overflowY: "auto",
          padding: 16,
          boxSizing: "border-box",

          borderRight: "1px solid #ddd",
          backgroundColor: "#f9f9f9",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            padding: "16px 0",
            margin: "0 0 24px 0",
            backgroundColor: theme.buttonColor,
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Theme History
        </h2>

        {/* Theme Slider Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            padding: "20px",
            marginBottom: "20px",
            position: "relative",
          }}
        >
          {/* Theme Slider Header*/}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            {/* Label + Counter in one line */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#555",
                }}
              >
                Theme Slider –
              </span>
              {(() => {
                const list = Array.isArray(themeState?.theme?.data)
                  ? themeState.theme.data
                  : Array.isArray(themeState?.theme)
                  ? themeState.theme
                  : [];

                const currentTheme = list[currentIndex];

                return (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 8,
                        background: theme.buttonColor,
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      Slide {currentIndex + 1} of {list.length}
                    </span>

                    {currentTheme?.active === true && (
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 8,
                          background: "green",
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        This is active theme
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handlePrev}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  background: theme.buttonColor,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                title="Show previous theme"
              >
                ◀ Back
              </button>
              <button
                onClick={handleNext}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  background: theme.buttonColor,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                title="Show next theme"
              >
                Next ▶
              </button>
            </div>
          </div>
        </div>

        {/* Individual Colors Section - Compact Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {colorConfigs.map(({ key, label }) => (
            <div
              key={key}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                border: "2px solid transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                e.currentTarget.style.borderColor = theme.buttonColor + "40";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                e.currentTarget.style.borderColor = "transparent";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <input
                  type="color"
                  value={theme[key]}
                  onChange={(e) => handleColorChange(key, e.target.value)}
                  style={{
                    width: "48px",
                    height: "48px",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                      marginBottom: "4px",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {theme[key]}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gradient Container Colors Section - Simplified */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#333",
                margin: 0,
              }}
            >
              Gradient Containers
            </h3>
          </div>
          
          {[
            {
              label: "Scaffold",
              containerKey: "scaffoldGradientColors",
              icon: "🏠",
            },
            {
              label: "Small",
              containerKey: "smallContainerGradientColors",
            },
            {
              label: "Medium",
              containerKey: "mediumContainerGradientColors",
            },
            {
              label: "Big",
              containerKey: "bigContainerGradientColors",
              icon: "",
            },
          ].map(({ label, containerKey, icon }) => {
            const gradientColors = theme[containerKey] || [];
            const isScaffold = containerKey === "scaffoldGradientColors";
            const gradientFunction = isScaffold ? buildScaffoldGradientString : buildGradientString;
            return (
              <div
                key={label}
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {icon && <span style={{ fontSize: "18px" }}>{icon}</span>}
                    <span style={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                      {label} {isScaffold ? "" : "Container"}
                    </span>
                  </div>
                  <button
                    onClick={() => addGradientColor(containerKey)}
                    style={{
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#fff",
                      background: `linear-gradient(135deg, ${theme.buttonColor}, ${theme.buttonColor}dd)`,
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    + Add Color
                  </button>
                </div>

                {/* Color Pickers - Compact Horizontal */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  {gradientColors.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#f8f9fa",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = theme.buttonColor + "40";
                        e.currentTarget.style.background = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "transparent";
                        e.currentTarget.style.background = "#f8f9fa";
                      }}
                    >
                      <input
                        type="color"
                        value={color}
                        onChange={(e) =>
                          handleGradientColorChange(containerKey, index, e.target.value)
                        }
                        style={{
                          width: "40px",
                          height: "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          fontFamily: "monospace",
                          minWidth: "65px",
                        }}
                      >
                        {color}
                      </div>
                      {gradientColors.length > 2 && (
                        <button
                          onClick={() => removeGradientColor(containerKey, index)}
                          style={{
                            padding: "4px 8px",
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#fff",
                            background: "#ff4444",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#ff0000";
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#ff4444";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title="Remove color"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls Section */}
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            position: "sticky",
            bottom: 16,
          }}
        >
          {canCreateTheme && (
            <button
              onClick={saveTheme}
              disabled={loading}
              style={{
                padding: "12px 32px",
                fontSize: 16,
                fontWeight: "600",
                color: "#fff",
                background: theme.buttonColor,
                border: "none",
                borderRadius: 8,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                transition: "transform 0.2s ease",
              }}
            >
              {loading ? "Saving..." : "Save Active Theme"}
            </button>
          )}
        </div>
      </div>

      {/* Preview panel */}
      <div
        style={{
          flex: 1.2,
          backgroundColor: "#f3f3f3",
          padding: 16,
          overflowY: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "360px",
            height: "700px",
            background: buildScaffoldGradientString(theme.scaffoldGradientColors) || theme.scaffoldBackgroundColor,
            borderRadius: 25,
            border: "8px solid #333",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 15px 40px rgba(0,0,0,0.4)",
            marginTop: "40px",
          }}
        >
          {/* Status Bar */}
          <div
            style={{
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 20px",
              color: theme.textColor,
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <span>1:09</span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>●●●●</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>
          {/* Profile Section / App Bar */}
          <div
            style={{
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: theme.appBarColor,
            }}
          >
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                background: theme.buttonColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.textColor,
                fontWeight: "bold",
                fontSize: "10px",
              }}
            >
              V
            </div>
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: `2px solid ${theme.buttonColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.textColor,
                background: "transparent",
                fontSize: "10px",
              }}
            >
              🔔
            </div>
          </div>
          <HoldingsSection
            containerColor={theme.containerColor}
            containerBorderColor={theme.containerBorderColor}
            valueAmount="504.00"
            cashAmount="0.00"
            textColor={theme.textColor}
            gradientColors={theme.mediumContainerGradientColors}
          />
          <GoldCard
            textcolor={theme.textColor}
            containerColor={theme.containerColor}
            containerBorderColor={theme.containerBorderColor}
            metalName="Gold"
            purity="24K-999"
            pricePerGram="9000.0"
            totalValue="504.00"
            weight="0.056 g"
            metalColor="#FFD700"
            gradientColors={theme.mediumContainerGradientColors}
          />
          {/* Trading Section */}
          <div
            style={{
              background: buildGradientString(theme.bigContainerGradientColors),
              margin: "0 10px",
              borderRadius: "20px",
              padding: "10px",
              border: `1px solid ${theme.containerBorderColor}`,
              opacity: 0.9,
            }}
          >
            {/* Gold Selection */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <button
                style={{
                  flex: 1,
                  padding: "10px",
                  background: theme.buttonColor,
                  border: "none",
                  borderRadius: "25px",
                  flexDirection: "column",
                  color: theme.textColor,
                  fontWeight: "600",
                }}
              >
                Gold 24K-999
              </button>
            </div>

            {/* Input Fields */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: `1px solid ${theme.buttonColor}`,
                  borderRadius: "10px",
                  color: theme.textColor,
                  fontSize: "10px",
                }}
                placeholder="Enter Grams"
              />
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  background: theme.buttonColor,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "center",
                  color: theme.textColor,
                }}
              >
                ⇄
              </div>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: `1px solid ${theme.buttonColor}`,
                  borderRadius: "10px",
                  color: theme.textColor,
                  fontSize: "10px",
                }}
                placeholder="Enter Amount"
              />
            </div>

            {/* Amount Buttons */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
              {["₹ 101", "₹ 501", "₹ 1001", "₹ 5001"].map((amount) => (
                <button
                  key={amount}
                  style={{
                    flex: 1,
                    padding: "8px",
                    background: "transparent",
                    border: `1px solid ${theme.buttonColor}`,
                    borderRadius: "20px",
                    color: theme.textColor,
                    fontSize: "10px",
                  }}
                >
                  {amount}
                </button>
              ))}
            </div>

            {/* Price Info */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  marginBottom: "8px",
                }}
              >
                Current Price (exclusive of 3% gst):
              </div>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                ₹ 9000.0/gm
              </div>
            </div>

            {/* Buy Button */}
            <button
              style={{
                width: "100%",
                padding: "10px",
                background: theme.buttonColor,
                border: "none",
                borderRadius: "25px",
                color: theme.textColor,
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Buy
            </button>
          </div>
          <div>
            <DiamondPointsCard 
              textcolor={theme.textColor} 
              containerColor={theme.containerColor}
              containerBorderColor={theme.containerBorderColor}
              gradientColors={theme.mediumContainerGradientColors}
            />
          </div>
          {/* Bottom Navigation */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "70px",
              borderRadius: "20px",
              border: `1px solid ${theme.containerBorderColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              background: theme.bottomNavBarBackgroundColor,
              opacity: 0.95,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏠
              </div>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "12px",
                }}
              >
                Home
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                💼
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Savings
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarSelectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🛒
              </div>
              <div
                style={{
                  color: theme.bottomNavBarSelectedColor,
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Buy
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏪
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Store
              </div>
            </div>
            <div style={{ textAlign: "center", position: "relative" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🧭
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Explore
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  right: "8px",
                  width: "8px",
                  height: "8px",
                  background: "#ff4444",
                  borderRadius: "50%",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
